import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { QRCodeSVG } from 'qrcode.react'

import {
  formatShortDate,
  getStartDateFormatter,
  getTimezoneDate,
  sort,
  timezones,
} from '../../utilities/helpers'

import Stack from 'react-bootstrap/Stack'
import Button from 'react-bootstrap/Button'

import { hasEventEnded } from '../PackageEventList/PackageEventList'

function formatSeatPosition(ticket) {
  if (!ticket) return null

  if (ticket?.generalAdmission) {
    return ticket?.sectionNumber ? `GA ${ticket.sectionNumber}` : 'GA'
  }

  const parts = []
  if (ticket?.sectionNumber) parts.push(`Sec ${ticket.sectionNumber}`)
  if (ticket?.rowNumber) parts.push(`Row ${ticket.rowNumber}`)
  if (ticket?.seatNumber) parts.push(`Seat ${ticket.seatNumber}`)
  return parts.join(', ') || null
}

function getRowStatus(event, ticket) {
  if (event?.status === 'complete' || ticket?.wasScanned) return 'attended'
  if (!ticket) return hasEventEnded(event) ? 'attended' : 'transferred'
  if (ticket?.transferred) return 'transferred'
  return 'upcoming'
}

function getStatusLabel(status) {
  if (status === 'attended') return 'Attended'
  if (status === 'transferred') return 'Transferred'
  return 'Upcoming'
}

function buildTicketRows(packageEvents) {
  const rows = []

  sort(packageEvents, 'event')?.forEach((packageEvent) => {
    const eventObj = packageEvent?.event || packageEvent

    if (packageEvent?.tickets?.length) {
      packageEvent.tickets.forEach((ticket) => {
        rows.push({
          key: `${eventObj?.uuid}-${ticket?.id || ticket?.checkInCode}`,
          event: eventObj,
          ticket,
          status: getRowStatus(eventObj, ticket),
        })
      })
      return
    }

    rows.push({
      key: eventObj?.uuid,
      event: eventObj,
      ticket: null,
      status: getRowStatus(eventObj, null),
    })
  })

  return rows
}

function buildStatusSummary(rows) {
  const counts = rows.reduce(
    (acc, row) => {
      acc[row.status] += 1
      return acc
    },
    { upcoming: 0, attended: 0, transferred: 0 }
  )

  const parts = []
  if (counts.upcoming) {
    parts.push(`${counts.upcoming} upcoming`)
  }
  if (counts.attended) {
    parts.push(`${counts.attended} attended`)
  }
  if (counts.transferred) {
    parts.push(`${counts.transferred} transferred`)
  }

  return parts.join(' · ')
}

function GameTicketRow({ row, linkTo, timezone }) {
  const { event, ticket, status } = row
  const eventStart = getTimezoneDate(event?.start, timezone)
  const seatLabel = formatSeatPosition(ticket)
  const dateLabel = eventStart
    ? formatShortDate(eventStart, getStartDateFormatter(event))
    : null
  const metaParts = [dateLabel, seatLabel].filter(Boolean)

  const content = (
    <>
      {ticket?.checkInCode ? (
        <div className="package-pass-tickets__event-qr-wrap">
          <div className="package-pass-tickets__event-qr">
            <QRCodeSVG value={ticket.checkInCode} size={48} />
          </div>
          {linkTo && ticket && (
            <span className="package-pass-tickets__event-qr-label">View</span>
          )}
        </div>
      ) : (
        <div
          className="package-pass-tickets__event-qr"
          style={{ width: 48, height: 48 }}
          aria-hidden="true"
        />
      )}
      <div className="flex-grow-1 min-w-0">
        <p className="package-pass-tickets__event-name">{event?.name}</p>
        {metaParts.length > 0 && (
          <p className="package-pass-tickets__event-meta">
            {metaParts.join(' · ')}
          </p>
        )}
      </div>
      {linkTo && ticket && (
        <span className="package-pass-tickets__event-action" aria-hidden="true">
          View ticket →
        </span>
      )}
      <span
        className={`package-pass-tickets__status-badge package-pass-tickets__status-badge--${status}`}
      >
        {getStatusLabel(status)}
      </span>
    </>
  )

  if (linkTo && ticket) {
    return (
      <Link
        to={`${linkTo}/event/${event?.uuid}`}
        className="package-pass-tickets__event-row"
        aria-label={`View ticket for ${event?.name}`}
      >
        {content}
      </Link>
    )
  }

  return (
    <div className="package-pass-tickets__event-row package-pass-tickets__event-row--static">
      {content}
    </div>
  )
}

export default function GameTicketsCompactList({
  packageEvents,
  linkTo,
  onBack,
}) {
  const timezone = timezones(
    packageEvents?.[0]?.event?.venue?.timezone ||
      packageEvents?.[0]?.venue?.timezone
  )

  const rows = useMemo(() => buildTicketRows(packageEvents), [packageEvents])
  const statusSummary = buildStatusSummary(rows)

  return (
    <>
      <div className="package-pass-tickets__events-toolbar">
        <Button
          variant="outline-light"
          size="sm"
          className="m-0"
          onClick={onBack}
        >
          ← Back to pass
        </Button>
        {statusSummary && (
          <span className="package-pass-tickets__events-summary">
            {statusSummary}
          </span>
        )}
      </div>

      <p className="package-pass-tickets__events-hint">
        Tap a game to view its QR code and wallet options.
      </p>

      <Stack gap={2} as="ul" className="list-unstyled m-0">
        {rows.map((row) => (
          <li key={row.key}>
            <GameTicketRow row={row} linkTo={linkTo} timezone={timezone} />
          </li>
        ))}
      </Stack>
    </>
  )
}
